import {t} from "@lingui/core/macro";
import memoize from "lodash.memoize";

import Log, {Errors} from "@khan/logging";

// eslint-disable-next-line camelcase
import type {getPublicBadgesForProfiles_user_profile_badges} from "#profile-package/__graphql-types__/getPublicBadgesForProfiles.ts";

import {getContextCSBadges} from "./strings/context-cs-badges.ts";
import {getContextCurationNodeBadges} from "./strings/context-curation-node-badges.ts";
import {getContextExerciseBadges} from "./strings/context-exercise-badges.ts";
import {getContextFeedbackBadges} from "./strings/context-feedback-badges.ts";
import {getContextNoneBadges} from "./strings/context-none-badges.ts";
import {getContextTeamBadges} from "./strings/context-team-badges.ts";
import {getCustomBadges} from "./strings/custom-badges.ts";
import {generatePatronBadgeStrings} from "./strings/patron-badges.ts";

type Translation = {
    translatedDescription: string;
    translatedFullDescription: string;
};

export type BadgeInfo = {
    isTeaserIfUnowned?: boolean;
    description: string;
    extendedDescription: string;
};

export const getTextForBadge = (
    // eslint-disable-next-line camelcase
    badge: getPublicBadgesForProfiles_user_profile_badges,
    isOwned: boolean,
): Translation => getTextForBadgeInexact(badge, isOwned);

// Breaking this out due to the differences in badge object anatomy in REST and
// GraphQL while migration is in progress.
export const getTextForBadgeInexact = (
    {
        name,
        description,
        fullDescription,
    }: {
        name: string;
        description: string;
        fullDescription: string;
    },
    isOwned: boolean,
): Translation => {
    const badgeInfo = getBadgeData()[name];

    // If this happens then somebody has added or changed a badge on the
    // backend without updating the frontend code.
    if (!badgeInfo) {
        // TODO(jvoll): Maybe we'll want to downgrade this to a warning since
        // any new custom badges will likely not be added to the translation
        // list regularly. For rollout though I think it's useful to have this
        // logged as an error to ensure I haven't missed any badges.
        Log.error(
            `No BadgeInfo found for badge slug '${name}'. Falling back to english.`,
            Errors.Internal,
        );
        return {
            translatedDescription: description,
            translatedFullDescription: fullDescription,
        };
    }

    const safeFullDescription =
        badgeInfo.isTeaserIfUnowned && !isOwned
            ? t`???`
            : badgeInfo.extendedDescription;

    return {
        translatedDescription: badgeInfo.description,
        translatedFullDescription: safeFullDescription,
    };
};

const getBadgeData: () => Record<string, BadgeInfo> = memoize(() => ({
    // NOTE(jvoll): Because we are spreading objects with indexer properties
    // instead of explicit keys, TypeScript requies that we do the spread before
    // listing any explicit keys. The patron badges would belong in
    // contextNoneBadges but the script that generated the translation files
    // purposely excludes the patron badges so that we can generate the patron
    // badge strings dynamically on the frontend.
    ...generatePatronBadgeStrings(),

    ...getContextCSBadges(),
    ...getContextCurationNodeBadges(),
    ...getContextExerciseBadges(),
    ...getContextFeedbackBadges(),
    ...getContextNoneBadges(),
    ...getContextTeamBadges(),
    ...getCustomBadges(),
}));
